class PaymentsCustom extends HTMLElement {
  constructor() {
    super();
    this.paymentTermsElement = null;
    this.paymentData = null;
    this.productForm = null;
    this._observer = null;
    this._abortController = null;
  }

  connectedCallback() {
    this.paymentTermsElement = this.querySelector("shopify-payment-terms");
    this.productForm = this.closest("product-form") || document.querySelector("product-form");
    this._abortController = new AbortController();

    if (this.paymentTermsElement) {
      // Set initial variant-id if missing
      if (!this.paymentTermsElement.getAttribute("variant-id") && this.productForm?.currentVariant?.id) {
        this.paymentTermsElement.setAttribute("variant-id", this.productForm.currentVariant.id);
      }
      
      this.extractPaymentData();
      this.observeChanges();
      this.listenVariantChange();
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._abortController?.abort();
  }

  listenVariantChange() {
    document.addEventListener(
      "variant-change",
      (event) => {
        const newVariantId = event.detail?.variant?.id || this.productForm?.currentVariant?.id;
        if (!newVariantId) return;

        this.updateVariant(newVariantId);
      },
      { signal: this._abortController.signal },
    );
  }

  updateVariant(variantId) {
    if (!this.paymentTermsElement) return;

    this.paymentTermsElement.setAttribute("variant-id", variantId);
    
    // Update UI immediately with cached data
    if (this.paymentData) {
      this.updatePaymentDisplay();
    }
  }

  decodeHTMLEntities(text) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  }

  extractPaymentData() {
    const shopifyMeta = this.paymentTermsElement.getAttribute("shopify-meta");

    if (!shopifyMeta) return;

    try {
      const decodedMeta = this.decodeHTMLEntities(shopifyMeta);
      this.paymentData = JSON.parse(decodedMeta);
      this.updatePaymentDisplay();
    } catch (error) {
      console.error("Error parsing payment terms data:", error);
    }
  }

  updatePaymentDisplay() {
    const shopPayElement =
      this.querySelector("[data-payment-installment]") || this.querySelector(".shop-payments .whitespace-nowrap");

    if (!shopPayElement || !this.paymentData?.variants) return;

    const template = shopPayElement.dataset.startingAtText;
    if (!template) return;

    const variantId = this.paymentTermsElement.getAttribute("variant-id");
    const currentVariant = this.paymentData.variants.find((v) => String(v.id) === String(variantId));

    if (currentVariant?.eligible && currentVariant?.available && currentVariant?.price_per_term) {
      const newText = template.replace(/\{\{\s*price\s*\}\}/g, currentVariant.price_per_term);
      shopPayElement.textContent = newText;
    }
  }

  observeChanges() {
    this._observer = new MutationObserver((mutations) => {
      const hasMetaChange = mutations.some((mutation) => mutation.attributeName === "shopify-meta");

      if (hasMetaChange) {
        this.extractPaymentData();
      }
    });

    this._observer.observe(this.paymentTermsElement, {
      attributes: true,
      attributeFilter: ["shopify-meta"],
    });
  }

  getPaymentData() {
    return this.paymentData;
  }

  getFinancingPlans() {
    return this.paymentData?.financing_plans || [];
  }

  getCurrentVariantPayment() {
    const variantId = this.paymentTermsElement?.getAttribute("variant-id");
    return this.paymentData?.variants?.find((v) => v.id.toString() === variantId);
  }
}

customElements.get("payments-custom") || customElements.define("payments-custom", PaymentsCustom);
